/**
 * Super Admin Permissions Dashboard
 * 
 * Allows super admin to:
 * - View all permission profiles
 * - Create/edit custom permission profiles
 * - Manage user permissions
 * - Set company permission limits
 * - View permission audit logs
 */

import { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc, updateDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { COLLECTIONS } from '../../utils/constants';
import { MODULES, ACTIONS, PERMISSION_PROFILES, validatePermissionProfile } from '../../utils/permissions';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import PermissionGuard from '../../components/PermissionGuard';

export default function Permissions() {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState('profiles');
  const [profiles, setProfiles] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [companyLimits, setCompanyLimits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load data
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadProfiles(),
        loadUserPermissions(),
        loadCompanyLimits()
      ]);
    } catch (error) {
      console.error('Error loading permissions data:', error);
      setError('Error cargando datos de permisos');
    } finally {
      setLoading(false);
    }
  };

  const loadProfiles = async () => {
    const profilesQuery = query(
      collection(db, COLLECTIONS.PERMISSION_PROFILES),
      orderBy('name')
    );
    const snapshot = await getDocs(profilesQuery);
    const profilesData = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setProfiles(profilesData);
  };

  const loadUserPermissions = async () => {
    const permissionsQuery = query(
      collection(db, COLLECTIONS.USER_PERMISSIONS),
      orderBy('updatedAt', 'desc')
    );
    const snapshot = await getDocs(permissionsQuery);
    const permissionsData = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setUserPermissions(permissionsData);
  };

  const loadCompanyLimits = async () => {
    const limitsQuery = query(
      collection(db, COLLECTIONS.COMPANY_PERMISSIONS),
      orderBy('updatedAt', 'desc')
    );
    const snapshot = await getDocs(limitsQuery);
    const limitsData = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setCompanyLimits(limitsData);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando sistema de permisos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-red-800 mb-2">Error</h2>
            <p className="text-red-600">{error}</p>
            <button
              onClick={loadAllData}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PermissionGuard requiredModule={MODULES.SUPER_ADMIN} requiredAction={ACTIONS.MANAGE}>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Sistema de Permisos</h1>
            <p className="mt-2 text-gray-600">
              Gestiona perfiles de permisos, asignaciones de usuarios y límites por empresa
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Perfiles</p>
                  <p className="text-2xl font-semibold text-gray-900">{profiles.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Usuarios con Permisos</p>
                  <p className="text-2xl font-semibold text-gray-900">{userPermissions.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Empresas con Límites</p>
                  <p className="text-2xl font-semibold text-gray-900">{companyLimits.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Módulos</p>
                  <p className="text-2xl font-semibold text-gray-900">{Object.keys(MODULES).length}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-lg shadow">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8 px-6">
                {[
                  { id: 'profiles', name: 'Perfiles de Permisos', icon: '🛡️' },
                  { id: 'users', name: 'Usuarios', icon: '👥' },
                  { id: 'companies', name: 'Límites por Empresa', icon: '🏢' },
                  { id: 'audit', name: 'Auditoría', icon: '📊' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <span className="mr-2">{tab.icon}</span>
                    {tab.name}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-6">
              {activeTab === 'profiles' && (
                <ProfilesTab 
                  profiles={profiles} 
                  onProfilesChange={loadProfiles}
                />
              )}
              {activeTab === 'users' && (
                <UsersTab 
                  userPermissions={userPermissions}
                  profiles={profiles}
                  onUserPermissionsChange={loadUserPermissions}
                />
              )}
              {activeTab === 'companies' && (
                <CompaniesTab 
                  companyLimits={companyLimits}
                  onCompanyLimitsChange={loadCompanyLimits}
                />
              )}
              {activeTab === 'audit' && (
                <AuditTab />
              )}
            </div>
          </div>
        </div>
      </div>
    </PermissionGuard>
  );
}

// Profiles Tab Component
function ProfilesTab({ profiles, onProfilesChange }) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);

  const systemProfiles = profiles.filter(p => p.isSystem);
  const customProfiles = profiles.filter(p => !p.isSystem);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Perfiles de Permisos</h3>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Crear Perfil
        </button>
      </div>

      {/* System Profiles */}
      <div>
        <h4 className="text-md font-medium text-gray-700 mb-3">Perfiles del Sistema</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {systemProfiles.map((profile) => (
            <ProfileCard 
              key={profile.id} 
              profile={profile} 
              onEdit={() => setEditingProfile(profile)}
              isSystem={true}
            />
          ))}
        </div>
      </div>

      {/* Custom Profiles */}
      {customProfiles.length > 0 && (
        <div>
          <h4 className="text-md font-medium text-gray-700 mb-3">Perfiles Personalizados</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {customProfiles.map((profile) => (
              <ProfileCard 
                key={profile.id} 
                profile={profile} 
                onEdit={() => setEditingProfile(profile)}
                onDelete={() => handleDeleteProfile(profile.id)}
                isSystem={false}
              />
            ))}
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingProfile) && (
        <ProfileModal
          profile={editingProfile}
          onClose={() => {
            setShowCreateModal(false);
            setEditingProfile(null);
          }}
          onSave={() => {
            setShowCreateModal(false);
            setEditingProfile(null);
            onProfilesChange();
          }}
        />
      )}
    </div>
  );
}

// Profile Card Component
function ProfileCard({ profile, onEdit, onDelete, isSystem }) {
  const moduleCount = profile.permissions?.length || 0;
  const actionCount = profile.permissions?.reduce((total, perm) => total + perm.actions.length, 0) || 0;

  return (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h5 className="font-medium text-gray-900">{profile.name}</h5>
          {isSystem && (
            <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded mt-1">
              Sistema
            </span>
          )}
        </div>
        <div className="flex space-x-1">
          <button
            onClick={onEdit}
            className="p-1 text-gray-400 hover:text-gray-600"
            title="Ver detalles"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          {!isSystem && onDelete && (
            <button
              onClick={onDelete}
              className="p-1 text-red-400 hover:text-red-600"
              title="Eliminar"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
        </div>
      </div>
      
      <p className="text-sm text-gray-600 mb-3">{profile.description}</p>
      
      <div className="flex justify-between text-xs text-gray-500">
        <span>{moduleCount} módulos</span>
        <span>{actionCount} acciones</span>
      </div>
    </div>
  );
}

// Placeholder components for other tabs
function UsersTab({ userPermissions, profiles, onUserPermissionsChange }) {
  return (
    <div className="text-center py-12">
      <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
      </svg>
      <h3 className="text-lg font-medium text-gray-900 mb-2">Gestión de Usuarios</h3>
      <p className="text-gray-600 mb-4">Próximamente: Asignar permisos a usuarios específicos</p>
      <div className="text-sm text-gray-500">
        {userPermissions.length} usuarios con permisos personalizados
      </div>
    </div>
  );
}

function CompaniesTab({ companyLimits, onCompanyLimitsChange }) {
  return (
    <div className="text-center py-12">
      <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
      <h3 className="text-lg font-medium text-gray-900 mb-2">Límites por Empresa</h3>
      <p className="text-gray-600 mb-4">Próximamente: Configurar límites de permisos por empresa</p>
      <div className="text-sm text-gray-500">
        {companyLimits.length} empresas con límites configurados
      </div>
    </div>
  );
}

function AuditTab() {
  return (
    <div className="text-center py-12">
      <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2H9a2 2 0 01-2-2z" />
      </svg>
      <h3 className="text-lg font-medium text-gray-900 mb-2">Auditoría de Permisos</h3>
      <p className="text-gray-600">Próximamente: Registro de cambios y reportes de auditoría</p>
    </div>
  );
}

// Profile Modal Component (placeholder)
function ProfileModal({ profile, onClose, onSave }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">
            {profile ? 'Ver Perfil' : 'Crear Perfil'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="text-center py-8">
          <p className="text-gray-600">Modal de creación/edición de perfiles próximamente</p>
          <button
            onClick={onClose}
            className="mt-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}